<template>
  <div
    class="gmb-attributes-mass-edit"
    :class="{ disabled: !canEditFacilityAttributes }"
  >
    <v-row class="max-w-560">
      <v-col class="d-flex align-center pb-0 max-w-50px">
        <v-checkbox
          class="mt-0"
          v-model="checkbox"
          @change="onCheckboxClicked($event)"
          :disabled="!canEditFacilityAttributes"
        ></v-checkbox>
      </v-col>
      <v-col class="d-flex align-center pb-0">
        <paragraph class="border-accent">
          {{ $t("message.facilityDetails.attributes.facilitiesAttributes") }}
        </paragraph>
      </v-col>
    </v-row>
    <v-row v-if="!attributes.length">
      <v-col class="max-w-50px" />
      <v-col>
        <div class="description">
          {{ $t("message.facilityDetails.attributes.noAttributesSelected") }}
        </div>
        <InfoBox
          class="mt-2"
          :text="$t('message.facilityDetails.attributes.editMessage')"
        />
        <div class="actions" v-if="checkbox">
          <div class="action" v-for="action in actions" :key="action.type">
            <div class="description muted">
              {{ $t(action.msg) }}
            </div>
            <v-btn v-bind="action.btnProps" @click="handleAction(action)">
              {{ $t(action.btnLabel) }}
            </v-btn>
            <v-tooltip right :max-width="tooltipMaxWidth">
              <template v-slot:activator="{ on }">
                <div class="info-icon" v-on="on">
                  <v-icon color="lime">mdi-information</v-icon>
                </div>
              </template>
              {{ $t(action.desc) }}
            </v-tooltip>
          </div>
        </div>
      </v-col>
    </v-row>
    <template v-if="attributes.length">
      <v-row>
        <v-col class="max-w-50px" />
        <v-col>
          <div class="active-action">
            <v-row>
              <v-col cols="11" class="d-flex align-center">
                <span class="section-title">{{ activeActionLabel }}:</span>
              </v-col>
              <v-col cols="1">
                <v-btn
                  icon
                  color="primary"
                  @click="handleAction(selectedAction, attributes)"
                >
                  <v-icon>
                    icon-edit
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="max-w-50px" />
        <v-col>
          <GmbAttributesPreview
            :facility-attributes="attributes"
            :delete-mode="selectedActionType === actionTypes.DELETE"
          ></GmbAttributesPreview>

          <div class="d-flex align-center mt-4">
            <div class="description muted mb-0">
              {{ $t("message.facilityDetails.attributes.changeType") }}
            </div>
            <v-btn
              v-for="action in notActiveActions"
              :key="action.type"
              text
              color="primary"
              @click="changeAction(action)"
              class="ml-4"
            >
              {{ $t(action.btnLabel) }}
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </template>

    <DialogGmbAttributesTree
      v-if="dialog"
      :facilities-ids="facilitiesIds"
      :facility-attributes="selectedAttributes"
      :is-open="dialog"
      :title="selectedAction.btnLabel"
      :message="selectedAction.desc"
      :delete-mode="selectedAction.type === actionTypes.DELETE"
      @save="handleSave"
      @close="dialog = false"
    />
  </div>
</template>

<script>
import Paragraph from "@/components/ui/Paragraph";
import DialogGmbAttributesTree from "@/components/FacilityDetails/GmbAttributes/GmbAttributesTree";
import GmbAttributesPreview from "@/components/FacilityDetails/GmbAttributes/GmbAttributesPreview";
import InfoBox from "@/components/ui/InfoBox.vue";

import { DialogHelper } from "@/helpers/dialog-helper";
import { GmbActionType } from "@/enums/gmb-attribute-action-type";
import { permissionsInfo } from "@/helpers/mixins/permissions-info";

export default {
  name: "GmbAttributesMassEdit",
  components: {
    Paragraph,
    DialogGmbAttributesTree,
    GmbAttributesPreview,
    InfoBox
  },
  mixins: [permissionsInfo],
  data() {
    return {
      dialog: false,
      checkbox: false,
      attributes: [],
      selectedAttributes: [],
      tooltipMaxWidth: 436,
      actions: [],
      facilitiesIds: [],
      actionTypes: GmbActionType,
      selectedAction: null,
      selectedActionType: null
    };
  },
  computed: {
    activeActionLabel() {
      const labelKey = "message.facilityDetails.attributes.";
      if (this.selectedActionType === this.actionTypes.ADD) {
        return this.$t(labelKey + "addingAttributes");
      } else if (this.selectedActionType === this.actionTypes.DELETE) {
        return this.$t(labelKey + "deletingAttributes");
      } else if (this.selectedActionType === this.actionTypes.SET) {
        return this.$t(labelKey + "overwritingAttributes");
      } else {
        return "";
      }
    },
    notActiveActions() {
      return this.actions.filter(
        ({ type }) => type !== this.selectedActionType
      );
    }
  },
  mounted() {
    this.facilitiesIds = this.$route.params.ids.split(",");
  },
  created() {
    this.setActions();
  },
  methods: {
    setActions() {
      this.actions = [
        {
          type: this.actionTypes.ADD,
          msg: "message.facilityDetails.attributes.addAttributesQuestion",
          desc: "message.facilityDetails.attributes.addAttributesDescription",
          btnLabel: "message.facilityDetails.attributes.addAttributes",
          btnProps: {
            color: "primary"
          }
        },
        {
          type: this.actionTypes.DELETE,
          msg: "message.facilityDetails.attributes.deleteAttributesQuestion",
          desc:
            "message.facilityDetails.attributes.deleteAttributesDescription",
          btnLabel: "message.facilityDetails.attributes.deleteAttributes",
          btnProps: {
            color: "primary"
          }
        },
        {
          type: this.actionTypes.SET,
          msg: "message.facilityDetails.attributes.overwriteAttributesQuestion",
          desc:
            "message.facilityDetails.attributes.overwriteAttributesDescription",
          btnLabel: "message.facilityDetails.attributes.overwriteAttributes",
          btnProps: {
            color: "primary",
            outlined: true
          }
        }
      ];
    },
    onCheckboxClicked(val) {
      this.$emit("update:checked", val);
      if (!val) {
        this.attributes = [];
        this.selectedAction = null;
        this.selectedActionType = null;
        this.$emit("update:attributes", this.attributes);
      }
    },
    changeAction(type) {
      DialogHelper.showConfirmDialog(
        {
          text: this.$t("message.facilityDetails.attributes.changeTypeConfirm"),
          okLabelKey: "message.common.continue"
        },
        () => {
          this.handleAction(type);
        }
      );
    },
    handleAction(action, attributes = []) {
      this.selectedAction = action;
      this.selectedAttributes = attributes;
      this.dialog = true;
    },
    handleSave(attributes) {
      this.attributes = attributes;
      this.selectedActionType = this.selectedAction.type;
      this.$emit("update:type", this.selectedActionType);
      this.$emit("update:attributes", this.attributes);
    }
  }
};
</script>

<style lang="scss" scoped>
.gmb-attributes-mass-edit {
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding-top: 12px;
  padding-bottom: 30px;
}

.section-title {
  @include font-size(16px);
  line-height: 24px;
  font-weight: 600;
  color: $grey-darker;
}

.description {
  @include font-size(14px);
  line-height: 17px;
  color: $grey-darker;
  margin-bottom: $spacing;

  &.muted {
    line-height: 19px;
    opacity: 0.7;
  }
}

.actions {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-right: -$spacing * 5.75;

  .action {
    flex-basis: 170px;
    position: relative;
    margin-top: $spacing * 5;
    margin-right: $spacing * 5.75;

    .description {
      text-align: center;
      margin-bottom: $spacing * 2.5;
    }

    .info-icon {
      display: inline;
      position: absolute;
      top: 6px;
      right: -30px;
    }
  }

  .v-btn {
    width: 100%;
  }
}

.active-action {
  border-bottom: 1px solid $light-3;
}

.max-w-560 {
  max-width: 560px;
}

.border-accent {
  @include borderAccent($lime);
}

.v-btn {
  text-transform: none;
}
</style>
